import Paper from '@modul-connect/shared/components/atoms/paper'
import Page from '@modul-connect/shared/components/atoms/page.v2'
import React, { useEffect, useState } from 'react'
import SearchWidget from '@modul-connect/shared/components/molecules/searchWidget.v2'
import TableWidget from '@modul-connect/shared/components/molecules/tableWidget'
import { userTeam_columns } from './team_columns'
import { useAuth0 } from '@auth0/auth0-react'
import { useDispatch, useSelector } from 'react-redux'
import { addUserToChildNode, moveUserToChildNode, removeUserFromOrganisation, updateUserPrivilege } from '../../../state/actions/customer'
import { EditPrivilegeModal } from './editPrivilegeModal'
import AddTeamUserModal from './addTeamUserModal'
import { CustomModal2 } from '@modul-connect/shared/components/molecules/modal/customModal2'
import View from '@modul-connect/shared/components/atoms/view'
import { P } from '@modul-connect/shared/components/atoms/text'
import { showWarningNotification } from '../../../state/actions/app'

export const TeamUser = ({ uuid, parent }) => {
  let filterTimer = null
  const [showAddUserModal, setShowAddUserModal] = React.useState(false)
  const [showAddExistingUserModal, setShowAddExistingUserModal] = React.useState(false)
  const [users, setUsers] = useState('')
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const loading = useSelector(state => state.loading)
  const [isDeleting, setIsDeleting] = useState(false)
  const [action, setAction] = useState(null)
  const [tableOptions, setTableOptions] = useState(null)
  
  const { getAccessTokenSilently } = useAuth0()
  const dispatch = useDispatch()
  const saving = useSelector(state => state.saving)
  const organisationNode = useSelector(state => state.organisation_node)
  const subtrees = useSelector(state => state.subtrees.subtrees)
  const userErrors = useSelector(state => state.organisation_node.userErrors)

  const users_prepared = organisationNode.uuid ? organisationNode.users.map(user => ({
    ...user,
    name: `${user.firstName} ${user.lastName}`,
  })) : []

  const findNodeByUuid = (nodes, uuid) => {
    if (!Array.isArray(nodes)) return null
    for (const node of nodes) {
      if (node?.uuid === uuid) return node
      const found = findNodeByUuid(node?.children, uuid)
      if (found) return found
    }
    return null
  }

  const node = findNodeByUuid(subtrees, organisationNode?.uuid)
  const orgName = node?.name

  const addUser = (newUser) => {
    getAccessTokenSilently()
      .then(token => {
        dispatch(addUserToChildNode( 
          token, 
          uuid, 
          newUser.firstName,
          newUser.lastName,
          newUser.email,
          newUser.privilege ?? 'standard' ))
      })
  }

  const moveUser = (user) => {
    setAction('move')
    setSelectedUser(user) 
    getAccessTokenSilently()
      .then(token => {
        dispatch(moveUserToChildNode( 
          token, 
          user.email,
          uuid, 
          user.privilege ?? 'standard',
          user.name,
          parent.uuid,
        ))
      }) 
  }

  useEffect(() => {
    if (!saving) return
  
    const allDone = saving.moveUserToChildNode?.length == 0
  
    const hasErrors = userErrors?.length > 0
  
    if (allDone && !hasErrors) {
      setShowAddUserModal(false)
      setShowAddExistingUserModal(false)
    }
  
    if (saving.updateUserPrivilege === null) {
      setShowEditModal(false)
    }
  }, [saving, userErrors])


  useEffect(() => {
    if (saving) {
      if (saving.moveUserToChildNode.length == 0) {
        let message = ''
        if (action === 'move') {
          message =  `Users were successfully added to ${orgName}.`
        }
        if (message) {
          dispatch(showWarningNotification(
            message,
            'success'
          ))
        }
      }
    }
  }, [saving.moveUserToChildNode])

  useEffect(() => {
    if (saving) {
      if (saving.removeUserFromOrganisation === null) {
        let message = ''
        if (action === 'remove') {
          message =  `${selectedUser?.name} was successfully deleted from ${orgName}.`
        }
        if (message) {
          dispatch(showWarningNotification(
            message,
            'success'
          ))
        }
      }
    }
  }, [saving.removeUserFromOrganisation])

  const handleNameChange = (event) => {
    setFilterAfterDelay(event.target.value)
  }

  const setFilterAfterDelay = (newFilter) => {
    if (filterTimer) {
      clearTimeout(filterTimer)
    }

    filterTimer = setTimeout(() => {
      setUsers(newFilter)
    }, 1000)
  }

  const sortByColumn = (data, column) => {
    if (!Array.isArray(data)) return data
      
    const isDesc = column.endsWith('desc')
    const key = isDesc ? column.replace(/desc$/i, '') : column
  
    return [...data].sort((a, b) => {
      const v1 = a[key] || ''
      const v2 = b[key] || ''
      return isDesc
        ? v2.localeCompare(v1, undefined, { numeric: true })
        : v1.localeCompare(v2, undefined, { numeric: true })
    })
  }

  const filteredUsers = [...users_prepared]
    .filter(t =>
      t.name?.toLowerCase().includes(users.toLowerCase()) ||
    t.organisation?.toLowerCase().includes(users.toLowerCase())
    )
  const sortKey = tableOptions?.sort || 'name'
  const sortedUsers = sortByColumn(filteredUsers, sortKey)
 

  const handleRowClick = (event, id, row) => {
    setShowEditModal(true)
    setSelectedUser(row)
  }
  const changeUserRole = (newRole) => {
    if (selectedUser.privilege === newRole.privilege) {
      setShowEditModal(false)
      return 
    }
    getAccessTokenSilently()
      .then(token => {
        dispatch(updateUserPrivilege(
          token,
          selectedUser.email,
          uuid,
          selectedUser.id,
          newRole.privilege ?? 'standard'
        ))
        
      })
  }
  
  const removeUser = (email, uuid, name) => {
    setAction('remove')
    getAccessTokenSilently().then(token => {
      dispatch(removeUserFromOrganisation(token, email, uuid, name))
    })
  }

  const usersToDisplay = !tableOptions ? sortedUsers : sortedUsers.
    slice(tableOptions.rowsPerPage * (tableOptions.currentpage -1), filteredUsers.length)

  return(
    <Page>
      <Paper title={'Users'} linkTitle={'Add user'} onClickLink={() => {
        setShowAddUserModal(true)}}>
        <SearchWidget
          onChange={handleNameChange}
          placeholder={'Search for user...'}
          initialValue={users}
        />
        <TableWidget
          data={usersToDisplay}
          columns={userTeam_columns}
          totalItems={filteredUsers?.length}
          defaultSort="name"
          searchStr={users}
          themes={{ device: 'desktop' }} 
          loadingStatus={loading?.getUsersFromNode}
          extend={{ width: '100%', paddingTop: 24 }}
          onRemove={(event, id) => {
            const row = filteredUsers.find(user => user.id === id)
            if (!row) return 
            setSelectedUser(row)  
            setIsDeleting(true)
          }}
          onFetchData={(options) => {
            setTableOptions((options))
          }}
          onClickRow={handleRowClick}
        />
        <EditPrivilegeModal
          openModal={showEditModal} 
          onSubmit={changeUserRole}
          onClose={() => setShowEditModal(false)}
          user={selectedUser}
          disabled={saving.updateUserPrivilege === 'saving'} />
        <AddTeamUserModal
          parent={parent}
          uuid={uuid}
          openModal={showAddUserModal}
          onClose={() => setShowAddUserModal(false)}
          addUser={addUser}
          moveUser={moveUser}
          isLoading={saving.moveUserToChildNode.some(x => x.state == 'saving')}
          disabled={saving.moveUserToChildNode.some(x => x.state == 'saving')}
          addingFailed={saving.moveUserToChildNode.some(x => x.state == 'failed')}
        />
        <CustomModal2
          openModal={isDeleting}
          title='Are you sure?'
          primaryButton='Yes'
          secondaryButton='Cancel'
          onSave={ () => {
            if (!selectedUser) return
            removeUser(selectedUser.email, uuid, parent)
            setIsDeleting(false)
          }}
          onCancel={() => setIsDeleting(false)}
          isPrimaryColor={false}
        >
          <View extend={{ '& strong': { fontWeight: 600 } }}>
            <P noPadding>
                  You're about to <strong>Delete</strong> <strong>{selectedUser?.name}</strong> from <strong>{orgName}</strong>.
            </P>
          </View>
        </CustomModal2>
      </Paper>
    </Page>
  )

}